import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { supabase } from '../services/supabase';
import { CoachingOnboarding } from './CoachingOnboarding';

export function SimpleWelcome() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [showCoachingIntake, setShowCoachingIntake] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkIfCompleted();
  }, []);

  async function checkIfCompleted() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, welcome_completed')
        .eq('id', user.id)
        .maybeSingle();

      if (profile?.welcome_completed) {
        navigate('/app');
        return;
      }

      if (profile?.display_name) {
        setName(profile.display_name);
      }
      setIsLoading(false);
    } catch (err) {
      console.error('Error checking welcome status:', err);
      setIsLoading(false);
    }
  }

  async function handleNameSubmit() {
    if (!name.trim()) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('profiles')
        .update({
          display_name: name.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      setShowCoachingIntake(true);
    } catch (err) {
      console.error('Error saving name:', err);
    }
  }

  function handleOnboardingComplete() {
    navigate('/app');
  }

  async function handleSkip() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({
            welcome_completed: true,
            coaching_onboarding_completed: true,
            onboarding_step: 5,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      }
    } catch (err) {
      console.error('Error skipping welcome:', err);
    }
    navigate('/app');
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-coral-500 border-t-transparent" />
      </div>
    );
  }

  if (showCoachingIntake) {
    return (
      <CoachingOnboarding
        userName={name}
        onComplete={handleOnboardingComplete}
        onSkip={handleSkip}
      />
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-8 sm:p-10">
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-coral-50 rounded-2xl flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-coral-500" />
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl font-heading font-bold text-ink-950">
                Welcome to Vzmo
              </h1>
              <p className="text-gray-600">
                Your personal coaching journey starts here
              </p>
            </div>

            <div className="pt-2 space-y-4">
              <div className="text-left">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  What should we call you?
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
                  placeholder="Your first name"
                  className="w-full px-4 py-3 text-lg rounded-xl border border-gray-200 focus:border-coral-400 focus:ring-2 focus:ring-coral-400/20 focus:outline-none transition-all"
                  autoFocus
                />
              </div>

              <button
                onClick={handleNameSubmit}
                disabled={!name.trim()}
                className="w-full py-3.5 px-6 bg-coral-500 text-white text-base font-semibold rounded-xl hover:bg-coral-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-coral-500/25 hover:shadow-xl hover:shadow-coral-500/30 hover:-translate-y-0.5"
              >
                Continue
              </button>

              <button
                onClick={handleSkip}
                className="w-full py-2.5 px-6 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors"
              >
                Skip and start using VZMO
              </button>
            </div>

            <p className="text-xs text-gray-400 pt-2">
              Takes about 60 seconds to get set up
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
